/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { EuiFlexGroup, EuiFlexItem } from "@inscopix/ideas-eui";
import {
  CellValue,
  Group,
  IdeasFile,
  RowIdentifier,
} from "@inscopix/ideas-hyperformula";
import { CallOutError } from "components/CallOutError/CallOutError";
import { CellEditorFileQuery, useCellEditorFileQuery } from "graphql/_Types";
import { chain, isEmpty, mapValues } from "lodash";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { FILE_TYPES_BY_ID, FileType } from "types/FileTypes";
import { CellEditorFileBodyFilter } from "./CellEditorFileBodyFilter";
import { CellValueRendererRowIdentifier } from "pages/gdt/cell-value-renderers/CellValueRendererRowIdentifier";
import {
  doesFileMatchFormat,
  flattenCellValueGroup,
} from "pages/gdt/cell-renderers/CellRendererBase.helpers";
import { FileSource } from "types/constants";
import { getTagsList } from "utils/getTagsList";

/** Type guard to check if a value is an `IdeasFile` instance. */
export const isIdeasFile = <T,>(value: T | IdeasFile): value is IdeasFile => {
  return value instanceof IdeasFile;
};

/**
 * Gets every file referenced in a cell value.
 * @param value
 * @returns An array of `IdeasFile` cell values.
 */
export const getFilesFromCellValue = (value: CellValue) => {
  if (isIdeasFile(value)) {
    return [value];
  } else if (value instanceof Group) {
    return flattenCellValueGroup(value).filter(isIdeasFile);
  } else {
    return [];
  }
};

export const sortFilesByNumberOfMatchingTagsOrTypes = (
  files:
    | NonNullable<CellEditorFileQuery["project"]>["files"]["nodes"]
    | undefined,
  fileTypes: FileType["id"][],
) => {
  if (!files) return files;

  const typeNames = fileTypes.map((typeId) => {
    const fileType = FILE_TYPES_BY_ID[typeId];
    return fileType?.name;
  }) as string[];

  return files.slice().sort((a, b) => {
    const aTags = getTagsList(a.tags, [a.fileType]);
    const aMatchingTagsCount = aTags.filter((tag) =>
      typeNames.includes(tag),
    ).length;
    const bTags = getTagsList(b.tags, [b.fileType]);
    const bMatchingTagsCount = bTags.filter((tag) =>
      typeNames.includes(tag),
    ).length;

    return bMatchingTagsCount - aMatchingTagsCount;
  });
};

const styles = {
  root: css`
    height: 320px;

    & > * {
      border-left: 1px solid #d3dae6;
      padding-left: 8px;
    }

    & > *:first-child {
      border: 0;
      padding: 0;
    }
  `,
};

const FILE_FILTERS = [
  "All Files",
  "Unassigned Files",
  "Uploaded Files",
  "Analysis Result Files",
] as const;

interface CellEditorFileBodyProps {
  fileTypes: FileType["id"][];
  fileFormats: string[];
  hideInvalidFiles: boolean;
  initialFileFilter: "all" | "unassigned";
  selectedTableId: string | undefined;
  selectedRowId: string | undefined;
  selectedColumnId: string | undefined;
  selectedFiles: {
    id: string;
    referenceId: string;
  }[];
  allowMultiSelect: boolean;
  setSelectedTableId: Dispatch<SetStateAction<string | undefined>>;
  setSelectedRowId: Dispatch<SetStateAction<string | undefined>>;
  setSelectedColumnId: Dispatch<SetStateAction<string | undefined>>;
  setSelectedFiles: Dispatch<
    SetStateAction<
      {
        id: string;
        referenceId: string;
      }[]
    >
  >;
}

export const CellEditorFileBody = ({
  fileTypes,
  fileFormats,
  hideInvalidFiles,
  initialFileFilter,
  selectedTableId,
  selectedRowId,
  selectedColumnId,
  selectedFiles,
  allowMultiSelect,
  setSelectedTableId,
  setSelectedRowId,
  setSelectedColumnId,
  setSelectedFiles,
}: CellEditorFileBodyProps) => {
  const projectId = useDataTableContext((s) => s.project.id);
  const [fileFilter, setFileFilter] = useState<
    (typeof FILE_FILTERS)[number] | undefined
  >(initialFileFilter === "all" ? "All Files" : "Unassigned Files");

  // Fetch all active files in the project
  const { data, loading } = useCellEditorFileQuery({
    fetchPolicy: "cache-and-network",
    variables: { projectId },
  });

  // Create file lookup maps
  const { lookupMap, sparseLookupMap } = useDataTableContext((s) => {
    // Map for looking up files by table ID -> row ID -> column ID. Paths without
    // valid files are INCLUDED.
    const lookupMap = chain(s.tables)
      .keyBy((table) => table.id)
      .mapValues((table) => ({
        key: table.id,
        label: table.name,
        kind: table.kind,
        rows: chain(table.rows)
          .keyBy((row) => row.id)
          .mapValues((row) => ({
            key: row.id,
            label: `${table.key}-${row.index}`,
            columns: chain(row.cells)
              .map((cell, idx) => {
                const column = table.columns[idx];
                return { ...cell, column };
              })
              .keyBy((cell) => cell.column.id)
              .mapValues((cell) => {
                const files = getFilesFromCellValue(cell.value);
                return {
                  key: cell.column.id,
                  label: cell.column.name,
                  files,
                };
              })
              .value(),
          }))
          .value(),
      }))
      .value();

    // Map for looking up files by table ID -> row ID -> column ID. Paths without
    // valid files are OMITTED.
    const sparseLookupMap = chain(lookupMap)
      .mapValues((table) => ({
        ...table,
        rows: chain(table.rows)
          .mapValues((row) => ({
            ...row,
            columns: chain(row.columns)
              .mapValues((column) => ({
                ...column,
                files: column.files.filter((file) => {
                  return doesFileMatchFormat(file.attrs, fileFormats);
                }),
              }))
              .pickBy((column) => column.files.length > 0)
              .value(),
          }))
          .pickBy((row) => !isEmpty(row.columns))
          .value(),
      }))
      .pickBy((table) => !isEmpty(table.rows))
      .value();

    return { lookupMap, sparseLookupMap };
  });

  // Determine which lookup map to use based on the active filters
  const selectedLookupMap = hideInvalidFiles ? sparseLookupMap : lookupMap;

  // Create an array of all selectable files based on the active filters
  const { files, fileCountsByFilter } = useMemo(() => {
    // Create a list of files matching the column file type
    const unSortedFiles = data?.project?.files.nodes.filter((file) => {
      return !hideInvalidFiles || doesFileMatchFormat(file, fileFormats);
    });
    // sort files by number of matching tags (or types)
    const files = sortFilesByNumberOfMatchingTagsOrTypes(
      unSortedFiles,
      fileTypes,
    );

    // Create a set containing the IDs of all files assigned to data table cells
    const assignedFileIds = new Set<string>();
    for (const tableId in selectedLookupMap) {
      const table = selectedLookupMap[tableId];
      if (table.kind === "data") {
        for (const rowId in table.rows) {
          const row = table.rows[rowId];
          for (const columnId in row.columns) {
            const column = row.columns[columnId];
            for (const file of column.files) {
              assignedFileIds.add(file.attrs.id);
            }
          }
        }
      }
    }

    const filesByFilter = {
      "All Files": files,

      "Unassigned Files": files?.filter(
        (file) =>
          !assignedFileIds.has(file.id) && file.source === FileSource.UPLOADED,
      ),

      "Uploaded Files": files?.filter(
        (file) => file.source === FileSource.UPLOADED,
      ),

      "Analysis Result Files": files?.filter(
        (file) => file.source === FileSource.ANALYSIS_RESULT,
      ),
    };

    return {
      files: fileFilter !== undefined ? filesByFilter[fileFilter] : [],
      fileCountsByFilter: mapValues(
        filesByFilter,
        (files) => files?.length ?? 0,
      ),
    };
  }, [
    data?.project?.files.nodes,
    fileTypes,
    fileFilter,
    hideInvalidFiles,
    fileFormats,
    selectedLookupMap,
  ]);

  /**
   * Selects a file if not previously selected.
   * @param file
   */
  const selectFile = (file: {
    id: string;
    referenceId: string;
    isSeries: boolean;
  }) => {
    setSelectedFiles((prevFiles) => {
      const isFileSelected = prevFiles.some(({ id }) => id === file.id);
      if (isFileSelected) {
        return prevFiles;
      } else if (allowMultiSelect) {
        return [...prevFiles, file];
      } else {
        return [file];
      }
    });
  };

  /**
   * Deselects a file if previously selected.
   * @param fileId
   */
  const deselectFile = (fileId: string) => {
    setSelectedFiles((file) => file.filter(({ id }) => id !== fileId));
  };

  /**
   * Selects a column. If there is only valid file matching the current filters,
   * auto-selects the file.
   * @param tableId
   * @param rowId
   * @param columnId
   */
  const selectColumn = (tableId: string, rowId: string, columnId: string) => {
    setSelectedColumnId(columnId);
    const files =
      selectedLookupMap[tableId].rows[rowId].columns[columnId].files;
    if (files.length === 1) {
      selectFile(files[0].attrs);
    }
  };

  /**
   * Selects a row. If there is only valid column matching the current filters,
   * auto-selects the column.
   * @param tableId
   * @param rowId
   */
  const selectRow = (tableId: string, rowId: string) => {
    setSelectedRowId(rowId);
    const columns = selectedLookupMap[tableId].rows[rowId].columns;
    if (Object.keys(columns).length === 1) {
      const columnId = Object.keys(columns)[0];
      selectColumn(tableId, rowId, columnId);
    } else {
      setSelectedColumnId(undefined);
    }
  };

  /**
   * Selects a table. If there is only valid row matching the current filters,
   * auto-selects the row.
   * @param tableId
   */
  const selectTable = (tableId: string) => {
    setSelectedTableId(tableId);
    const rows = selectedLookupMap[tableId].rows;
    if (Object.keys(rows).length === 1) {
      const rowId = Object.keys(rows)[0];
      selectRow(tableId, rowId);
    } else {
      setSelectedRowId(undefined);
      setSelectedColumnId(undefined);
    }
  };

  const isToolProject = data?.project?.isToolProject ?? false;

  return (
    <EuiFlexGroup css={styles.root} gutterSize="s">
      {/* Table filter */}
      {!isToolProject && (
        <EuiFlexItem>
          <CellEditorFileBodyFilter
            singleSelection
            searchPlaceholder="Filter tables"
            checkedIcon="arrow"
            options={[
              {
                label: "Files",
                isGroupLabel: true,
              },
              ...FILE_FILTERS.map((filter) => ({
                key: filter,
                label: `${filter} (${fileCountsByFilter[filter]})`,
                checked: fileFilter === filter,
                isGroupLabel: false,
              })),
              {
                label: "Data Tables",
                isGroupLabel: true,
              },
              ...Object.values(selectedLookupMap)
                .filter((table) => table.kind === "data")
                .map((table) => ({
                  key: table.key,
                  label: table.label,
                  checked: table.key === selectedTableId,
                  isGroupLabel: false,
                })),
              {
                label: "Analysis Tables",
                isGroupLabel: true,
              },
              ...Object.values(selectedLookupMap)
                .filter((table) => table.kind === "analysis")
                .map((table) => ({
                  key: table.key,
                  label: table.label,
                  checked: table.key === selectedTableId,
                  isGroupLabel: false,
                })),
            ]}
            onChange={(_options, _event, changedOption) => {
              if (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (FILE_FILTERS as readonly any[]).includes(changedOption.key)
              ) {
                setFileFilter(changedOption.key as typeof fileFilter);
                setSelectedTableId(undefined);
                setSelectedColumnId(undefined);
                setSelectedRowId(undefined);
              } else {
                const tableId = changedOption.key;
                if (tableId !== undefined) {
                  setFileFilter(undefined);
                  selectTable(tableId);
                }
              }
            }}
          />
        </EuiFlexItem>
      )}

      {/* Row filter */}
      {selectedTableId !== undefined && (
        <EuiFlexItem>
          <CellEditorFileBodyFilter
            singleSelection
            searchPlaceholder="Filter rows"
            checkedIcon="arrow"
            renderOption={(option) => {
              const [tableKey, rowIndex] = option.label.split("-");
              const rowIdentifier = new RowIdentifier(
                tableKey,
                parseInt(rowIndex),
              );
              return (
                <CellValueRendererRowIdentifier rowIdentifier={rowIdentifier} />
              );
            }}
            options={Object.values(selectedLookupMap[selectedTableId].rows).map(
              (row) => ({
                key: row.key,
                label: row.label,
                checked: row.key === selectedRowId,
                isGroupLabel: false,
              }),
            )}
            onChange={(_options, _event, changedOption) => {
              const rowId = changedOption.key;
              if (rowId !== undefined) {
                selectRow(selectedTableId, rowId);
              }
            }}
          />
        </EuiFlexItem>
      )}

      {/* Column filter */}
      {selectedTableId !== undefined && selectedRowId !== undefined && (
        <EuiFlexItem>
          <CellEditorFileBodyFilter
            singleSelection
            searchPlaceholder="Filter columns"
            checkedIcon="arrow"
            // TODO: Add group labels for column groups
            options={Object.values(
              selectedLookupMap[selectedTableId].rows[selectedRowId].columns,
            ).map((column) => ({
              key: column.key,
              label: column.label,
              checked: column.key === selectedColumnId,
              isGroupLabel: false,
            }))}
            onChange={(_options, _event, changedOption) => {
              const columnId = changedOption.key;
              if (columnId !== undefined) {
                selectColumn(selectedTableId, selectedRowId, columnId);
              }
            }}
          />
        </EuiFlexItem>
      )}

      {/* File filter */}
      {(fileFilter !== undefined || selectedColumnId !== undefined) && (
        <EuiFlexItem>
          <CellEditorFileBodyFilter
            singleSelection={false}
            searchPlaceholder="Filter files"
            checkedIcon="check"
            isLoading={files === undefined}
            errorMessage={
              files === undefined && !loading ? <CallOutError /> : undefined
            }
            options={
              fileFilter !== undefined && files !== undefined
                ? files.map((file) => ({
                    key: file.id,
                    data: file,
                    label: file.name,
                    checked: selectedFiles.some(({ id }) => id === file.id),
                    isGroupLabel: false,
                  }))
                : selectedTableId !== undefined &&
                    selectedRowId !== undefined &&
                    selectedColumnId !== undefined
                  ? Object.values(
                      selectedLookupMap[selectedTableId].rows[selectedRowId]
                        .columns[selectedColumnId].files,
                    ).map((file) => ({
                      key: file.attrs.id,
                      data: file.attrs,
                      label: file.attrs.name,
                      checked: selectedFiles.some(
                        ({ id }) => id === file.attrs.id,
                      ),
                      isGroupLabel: false,
                    }))
                  : []
            }
            onChange={(_options, _event, changedOption) => {
              const file = changedOption.data as {
                id: string;
                referenceId: string;
                isSeries: boolean;
              };
              if (file !== undefined) {
                if (changedOption.checked === "on") {
                  selectFile(file);
                } else {
                  deselectFile(file.id);
                }
              }
            }}
          />
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  );
};
